import React, { useCallback, useRef, useState } from "react";
import ReactModal from "react-modal";
import Webcam from "react-webcam";
import { FaStop, FaPlay, FaPause, FaTrash, FaCheck } from "react-icons/fa";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import Album from "../../assets/icons/Notes/album.svg";
import FlipCamera from "../../assets/icons/Notes/flipCamera.svg";
import CloseIcon from "../../assets/icons/Notes/closeIcon.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard, Mousewheel } from "swiper/modules";

// Helper to get cropped image as blob
function getCroppedImg(image, crop) {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      1
    );
  });
}

const NotesWebcam = ({ isOpen, onClose, addItem }) => {
  const webcamRef = useRef(null);
  const sliderRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showCrop, setShowCrop] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 100,
    height: 100,
    x: 0,
    y: 0,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [imageCaption, setImageCaption] = useState(""); // For image caption
  const [videoCaption, setVideoCaption] = useState(""); // For video caption
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoRecorded, setVideoRecorded] = useState(false);
  const [videoTimer, setVideoTimer] = useState(0);
  const [videoPlayer, setVideoPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [pickedVideoUrl, setPickedVideoUrl] = useState(null);
  const [pickedVideoFile, setPickedVideoFile] = useState(null);
  const [originalImageFile, setOriginalImageFile] = useState(null); // New state for original image file
  const [videoThumbnail, setVideoThumbnail] = useState(null); // Data URL
  const [videoThumbnailFile, setVideoThumbnailFile] = useState(null);
  const timerRef = useRef(null);


  const allTabs = ["Photo", "Video"];
  const videoConstraints = {
    width: 1200,
    height: 800,
    facingMode: "user",
  };

  // Format time as mm:ss
  const formatTime = (sec) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // --- VIDEO RECORDING ---
  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    setIsPaused(false);
    setRecordedChunks([]);
    setVideoRecorded(false);
    setVideoUrl(null);
    setVideoTimer(0);
    setVideoCaption("");
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/webm",
    });
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    mediaRecorderRef.current.start();
    timerRef.current = setInterval(() => {
      setVideoTimer((prev) => prev + 1);
    }, 1000);
  }, [webcamRef, setCapturing, mediaRecorderRef]);

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => prev.concat(data));
    }
  }, []);

  const handlePauseCapture = () => {
    if (mediaRecorderRef.current && capturing && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current);
    }
  };

  const handleResumeCapture = () => {
    if (mediaRecorderRef.current && capturing && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setVideoTimer((prev) => prev + 1);
      }, 1000);
    }
  };

  const handleStopCaptureClick = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setCapturing(false);
      setIsPaused(false);
      setVideoRecorded(true);
      clearInterval(timerRef.current);
    }
  }, [mediaRecorderRef, setCapturing]);

  const handleDiscardVideo = () => {
    setCapturing(false);
    setIsPaused(false);
    setRecordedChunks([]);
    setVideoRecorded(false);
    setVideoUrl(null);
    setVideoTimer(0);
    setVideoCaption("");
    setIsPlaying(false);
    if (videoPlayer) {
      videoPlayer.pause();
      videoPlayer.currentTime = 0;
    }
  };

  // When video is recorded, create URL
  React.useEffect(() => {
    if (videoRecorded && recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    }
  }, [videoRecorded, recordedChunks]);

  // --- IMAGE CAPTURE & CROP ---
  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCropImage(imageSrc);
    setShowCrop(true);
    setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
    setCompletedCrop(null);
    setImageCaption("");
  }, [webcamRef]);

  // --- FILE PICK & CROP ---
  const handleFileChange = async (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        setOriginalImageFile(file); // Store the original image file
        const reader = new FileReader();
        reader.onload = () => {
          setCropImage(reader.result);
          setShowCrop(true);
          setCrop({ unit: "%", width: 100, height: 100, x: 0, y: 0 });
          setCompletedCrop(null);
          setImageCaption("");
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith("video/")) {
        // Show confirm modal for picked video
        setPickedVideoFile(file);
        setPickedVideoUrl(URL.createObjectURL(file));
        setVideoCaption("");
      }
    }
  };


  // --- CROP HANDLERS ---
  const handleCropSave = async () => {
    if (imageRef) {
      if (!completedCrop || !completedCrop.width || !completedCrop.height) {
        // No crop: send original file
        if (originalImageFile || cropImage) {
          let file;
          if (cropImage) {
            const res = await fetch(cropImage);
            const blob = await res.blob();
            file = new File([blob], "image.jpg", { type: blob.type });
          } else {
            file = originalImageFile;
          }
          addItem(file, "image", imageCaption);
        }
      } else {
        // Cropped: use canvas
        const croppedBlob = await getCroppedImg(imageRef, completedCrop);
        const file = new File([croppedBlob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        addItem(file, "image", imageCaption);
      }
    }
    setShowCrop(false);
    setCropImage(null);
    setCompletedCrop(null);
    setImageRef(null);
    setImageCaption("");
    setOriginalImageFile(null);
    onClose(false);
  };

  const handleCropCancel = () => {
    setShowCrop(false);
    setCropImage(null);
    setCompletedCrop(null);
    setImageRef(null);
    setImageCaption("");
  };

  const handleSlideChange = (swiper) => {
    setCurrentSlide(swiper.activeIndex);
  };
  const handleSlideClick = (index) => {
    setCurrentSlide(index);
    sliderRef.current?.slideTo(index, 500);
  };

  // --- VIDEO SAVE ---
  const handleVideoSave = () => {
    if (recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, {
        type: "video/webm",
      });
      const file = new File([blob], "react-webcam-stream-capture.mp4", {
        type: "video/mp4",
      });
      addItem(
        file,
        "video",
        videoCaption,
        videoThumbnailFile // Pass thumbnail file as 4th argument if your addItem supports it
      );
      handleDiscardVideo();
      setVideoThumbnail(null);
      setVideoThumbnailFile(null);
      onClose(false);
    }
  };

  // --- VIDEO PLAYER CONTROLS ---
  const handlePlayPause = () => {
    if (!videoPlayer) return;
    if (isPlaying) {
      videoPlayer.pause();
    } else {
      videoPlayer.play();
    }
  };

  // Add a handler for saving picked video
  const handlePickedVideoSave = () => {
    if (pickedVideoFile) {
      addItem(
        pickedVideoFile,
        "video",
        videoCaption,
        videoThumbnailFile // Pass thumbnail file as 4th argument if your addItem supports it
      );
      setPickedVideoFile(null);
      setPickedVideoUrl(null);
      setVideoCaption("");
      setVideoThumbnail(null);
      setVideoThumbnailFile(null);
      onClose(false);
    }
  };

  // Add a handler for discarding picked video
  const handlePickedVideoDiscard = () => {
    setPickedVideoFile(null);
    setPickedVideoUrl(null);
    setVideoCaption("");
  };

  // Handler to pick thumbnail from file
  const handleThumbnailFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setVideoThumbnail(reader.result);
        setVideoThumbnailFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler to capture thumbnail from current video timestamp
  const handleCaptureThumbnail = () => {
    const video = document.getElementById("video-preview");
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], "thumbnail.jpg", {
            type: "image/jpeg",
          });
          setVideoThumbnail(URL.createObjectURL(blob));
          setVideoThumbnailFile(file);
        }
      },
      "image/jpeg",
      0.95
    );
  };

  // --- UI ---
  return (
    <>
      <ReactModal
        isOpen={isOpen}
        className={"overflow-hidden"}
        onRequestClose={() => {
          handleDiscardVideo();
          onClose(false);
        }}
      >
        <div className="relative flex h-[100vh] w-full flex-col items-center justify-center gap-6 bg-blackish">
          <div className="relative flex h-[80%] w-[90%] flex-col bg-black md:h-[90%] md:w-fit">
            {/* Image Crop UI */}
            <div
              className="absolute right-2 top-2 z-50 aspect-square cursor-pointer rounded-full bg-black bg-opacity-60 p-3"
              onClick={() => {
                onClose(false);
              }}
            >
              <img src={CloseIcon} alt="CloseIcon" className="h-5" />
            </div>

            {pickedVideoUrl ? (
              <div className="flex h-full w-full flex-col items-center justify-center">
                <video
                  id="video-preview"
                  src={pickedVideoUrl}
                  controls
                  className="w-full max-w-[500px] rounded-lg"
                  style={{ maxHeight: "60vh" }}
                />
                <input
                  type="text"
                  value={videoCaption}
                  onChange={(e) => setVideoCaption(e.target.value)}
                  placeholder="Add a caption (optional)"
                  className="mt-4 w-11/12 rounded-md border border-greyishV3 bg-black px-3 py-2 text-white"
                />
                {/* Thumbnail controls */}
                <div className="mt-4 flex flex-col items-center gap-2">
                  <div className="flex gap-3">
                    <button
                      className="rounded bg-blue-600 px-3 py-1 text-white"
                      type="button"
                      onClick={handleCaptureThumbnail}
                    >
                      Capture Thumbnail from Video
                    </button>
                    <label className="cursor-pointer rounded bg-blue-600 px-3 py-1 text-white">
                      Pick Thumbnail
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleThumbnailFileChange}
                      />
                    </label>
                  </div>
                  {videoThumbnail && (
                    <img
                      src={videoThumbnail}
                      alt="Video Thumbnail"
                      className="mt-2 h-24 rounded border border-greyishV3 object-contain"
                    />
                  )}
                </div>
                <div className="mt-4 flex gap-4 pb-12">
                  <button
                    className="flex items-center justify-center rounded-full bg-red-500 p-3 text-white shadow-lg transition hover:scale-105"
                    onClick={handlePickedVideoDiscard}
                    aria-label="Delete"
                  >
                    <FaTrash size={18} />
                  </button>
                  <button
                    className="flex items-center justify-center rounded-full bg-green-500 p-3 text-white shadow-lg transition hover:scale-105"
                    onClick={handlePickedVideoSave}
                    aria-label="Save"
                  >
                    <FaCheck size={18} />
                  </button>
                </div>
                {/* Bottom tabs for mode switch */}
                <div className="absolute bottom-0 flex h-20 w-full justify-center gap-5 rounded-tl-2xl rounded-tr-2xl bg-greyishV4 pt-3">
                  <Swiper
                    grabCursor={true}
                    slidesPerView={7}
                    spaceBetween={10}
                    centeredSlides={true}
                    className=""
                    modules={[Keyboard, Mousewheel]}
                    onSlideChange={handleSlideChange}
                    onSwiper={(swiper) => (sliderRef.current = swiper)}
                  >
                    {allTabs?.map((row, index) => (
                      <SwiperSlide
                        key={index}
                        className={`!w-fit ${
                          index === currentSlide
                            ? "text-xl text-[#F8BA34]"
                            : "text-xl text-[#A3A3A3]"
                        } `}
                      >
                        <p
                          onClick={() => {
                            handleSlideClick(index);
                          }}
                        >
                          {row}
                        </p>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            ) : showCrop ? (
              // Crop UI
              <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black bg-opacity-80">
                <div className="relative flex h-[60vw] w-[90vw] flex-col items-center justify-center rounded-xl bg-black">
                  <ReactCrop
                    crop={crop}
                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={undefined}
                    minWidth={10}
                    minHeight={10}
                  >
                    <img
                      src={cropImage}
                      alt="Crop"
                      style={{ maxWidth: "100%", maxHeight: "60vh" }}
                      onLoad={(e) => setImageRef(e.currentTarget)}
                    />
                  </ReactCrop>
                  <div className="mt-4 flex w-full flex-col items-center gap-2">
                    <input
                      type="text"
                      value={imageCaption}
                      onChange={(e) => setImageCaption(e.target.value)}
                      placeholder="Add a caption (optional)"
                      className="w-[47.5%] rounded-md border border-greyishV3 bg-black px-3 py-2 text-white"
                    />
                    <div className="z-50 mt-2 flex gap-4">
                      <button
                        className="rounded bg-red-500 px-4 py-2 text-white"
                        onClick={handleCropCancel}
                      >
                        Cancel
                      </button>
                      <button
                        className="rounded bg-green-500 px-4 py-2 text-white"
                        onClick={handleCropSave}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : currentSlide === 1 ? (
              // Video Recording UI
              <div className="relative flex h-full w-full flex-col items-center justify-center">
                {!videoRecorded && (
                  <div className="flex h-full w-full flex-col items-center justify-center">
                    <Webcam
                      style={{
                        height: "95%",
                        width: "100%",
                        borderRadius: "",
                      }}
                      audio={true}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      mirrored={false}
                    />
                    <div className="absolute left-4 top-4 flex items-center gap-2">
                      {capturing && (
                        <>
                          <span className="h-3 w-3 animate-pulse rounded-full bg-red-500"></span>
                          <span className="font-mono text-lg text-white">
                            {formatTime(videoTimer)}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Pick from gallery for video */}
                    <div className="absolute bottom-[15%] left-[11%] flex flex-col items-center gap-4 md:flex-row">
                      <div className="upload-btn-wrapper">
                        <button
                          className="flex cursor-pointer items-center gap-2 rounded-full bg-black bg-opacity-60 p-3 text-xs font-semibold text-red-200 transition-all duration-500 ease-in-out hover:bg-red-700"
                          onClick={() => inputRef.current.click()}
                        >
                          <img
                            src={Album}
                            alt="Album"
                            className="cursor-pointer"
                          />
                        </button>
                        <input
                          type="file"
                          ref={inputRef}
                          multiple={false}
                          onChange={handleFileChange}
                          name="myfile"
                          accept="video/mp4, video/webm, video/ogg"
                          className="hidden cursor-pointer"
                        />
                      </div>
                    </div>
                    <div className="absolute bottom-24 left-1/2 flex -translate-x-1/2 gap-4">
                      {!capturing && (
                        <button
                          className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-white bg-red-600 bg-opacity-80"
                          onClick={handleStartCaptureClick}
                        >
                          <FaStop size={20} className="invisible" />
                        </button>
                      )}
                      {capturing && (
                        <>
                          {!isPaused ? (
                            <button
                              className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-yellow-400 bg-yellow-600 bg-opacity-80"
                              onClick={handlePauseCapture}
                            >
                              <FaPause size={20} className="text-white" />
                            </button>
                          ) : (
                            <button
                              className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-yellow-400 bg-yellow-600 bg-opacity-80"
                              onClick={handleResumeCapture}
                            >
                              <FaPlay size={20} className="text-white" />
                            </button>
                          )}
                          <button
                            className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-red-500 bg-red-700 bg-opacity-80"
                            onClick={handleStopCaptureClick}
                          >
                            <FaStop size={20} className="text-white" />
                          </button>
                          <button
                            className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-gray-400 bg-gray-700 bg-opacity-80"
                            onClick={handleDiscardVideo}
                          >
                            <FaTrash size={20} className="text-white" />
                          </button>
                        </>
                      )}
                    </div>
                    {capturing && (
                      <input
                        type="text"
                        value={videoCaption}
                        onChange={(e) => setVideoCaption(e.target.value)}
                        placeholder="Add a caption (optional)"
                        className="absolute bottom-4 left-1/2 w-[60%] -translate-x-1/2 rounded-md border border-greyishV3 bg-black px-3 py-2 text-white"
                      />
                    )}
                    {/* Bottom tabs for mode switch */}
                    <div className="absolute bottom-0 flex h-20 w-full justify-center gap-5 rounded-tl-2xl rounded-tr-2xl bg-greyishV4 pt-3">
                      <Swiper
                        grabCursor={true}
                        slidesPerView={7}
                        spaceBetween={10}
                        centeredSlides={true}
                        className=""
                        modules={[Keyboard, Mousewheel]}
                        onSlideChange={handleSlideChange}
                        onSwiper={(swiper) => (sliderRef.current = swiper)}
                      >
                        {allTabs?.map((row, index) => (
                          <SwiperSlide
                            key={index}
                            className={`!w-fit ${
                              index === currentSlide
                                ? "text-xl text-[#F8BA34]"
                                : "text-xl text-[#A3A3A3]"
                            } `}
                          >
                            <p
                              onClick={() => {
                                handleSlideClick(index);
                              }}
                            >
                              {row}
                            </p>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                )}
                {videoRecorded && videoUrl && (
                  <div className="flex h-full w-full flex-col items-center justify-center">
                    <video
                      id="video-preview"
                      ref={setVideoPlayer}
                      src={videoUrl}
                      controls
                      className="w-full max-w-[500px] rounded-lg"
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                    />
                    <input
                      type="text"
                      value={videoCaption}
                      onChange={(e) => setVideoCaption(e.target.value)}
                      placeholder="Add a caption (optional)"
                      className="mt-4 w-11/12 rounded-md border border-greyishV3 bg-black px-3 py-2 text-white"
                    />
                    {/* Thumbnail controls */}
                    <div className="mt-4 flex flex-col items-center gap-2">
                      <div className="flex gap-3">
                        <button
                          className="rounded bg-blue-600 px-3 py-1 text-white"
                          type="button"
                          onClick={handleCaptureThumbnail}
                        >
                          Capture Thumbnail from Video
                        </button>
                        <label className="cursor-pointer rounded bg-blue-600 px-3 py-1 text-white">
                          Pick Thumbnail
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleThumbnailFileChange}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center mb-10 gap-8">
                      {videoThumbnail && (
                        <img
                          src={videoThumbnail}
                          alt="Video Thumbnail"
                          className="mt-2 h-24 rounded border border-greyishV3 object-contain"
                        />
                      )}
                      <div className="mt-4 flex gap-4">
                        <button
                          className="flex items-center justify-center rounded-full bg-red-500 p-3 text-white shadow-lg transition hover:scale-105"
                          onClick={handleDiscardVideo}
                          aria-label="Delete"
                        >
                          <FaTrash size={18} />
                        </button>
                        <button
                          className="flex items-center justify-center rounded-full bg-green-500 p-3 text-white shadow-lg transition hover:scale-105"
                          onClick={handleVideoSave}
                          aria-label="Save"
                        >
                          <FaCheck size={18} />
                        </button>
                      </div>
                    </div>
                    {/* Bottom tabs for mode switch */}
                    <div className="absolute bottom-0 flex h-20 w-full justify-center gap-5 rounded-tl-2xl rounded-tr-2xl bg-greyishV4 pt-3">
                      <Swiper
                        grabCursor={true}
                        slidesPerView={7}
                        spaceBetween={10}
                        centeredSlides={true}
                        className=""
                        modules={[Keyboard, Mousewheel]}
                        onSlideChange={handleSlideChange}
                        onSwiper={(swiper) => (sliderRef.current = swiper)}
                      >
                        {allTabs?.map((row, index) => (
                          <SwiperSlide
                            key={index}
                            className={`!w-fit ${
                              index === currentSlide
                                ? "text-xl text-[#F8BA34]"
                                : "text-xl text-[#A3A3A3]"
                            } `}
                          >
                            <p
                              onClick={() => {
                                handleSlideClick(index);
                              }}
                            >
                              {row}
                            </p>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Photo UI
              <>
                <Webcam
                  style={{
                    height: "93%",
                    width: "100%",
                    borderRadius: "",
                  }}
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={videoConstraints}
                  mirrored={false}
                />
                <div className="absolute bottom-[15%] left-[11%] flex flex-col items-center gap-4 md:flex-row">
                  <div className="upload-btn-wrapper">
                    <button
                      className="flex cursor-pointer items-center gap-2 rounded-full bg-black bg-opacity-60 p-3 text-xs font-semibold text-red-200 transition-all duration-500 ease-in-out hover:bg-red-700"
                      onClick={() => inputRef.current.click()}
                    >
                      <img src={Album} alt="Album" className="cursor-pointer" />
                    </button>
                    <input
                      type="file"
                      ref={inputRef}
                      multiple={false}
                      onChange={handleFileChange}
                      name="myfile"
                      accept={`${
                        currentSlide === 0
                          ? "image/jpeg, image/png, image/gif, image/svg+xml, image/webp"
                          : "video/mp4, video/webm, video/ogg"
                      }`}
                      className="hidden cursor-pointer"
                    />
                  </div>
                </div>
                <button
                  className="absolute bottom-[14%] left-1/2 -translate-x-1/2 rounded-full border-4 border-red-500 bg-red-800 bg-opacity-60 p-6 text-red-200"
                  onClick={capture}
                >
                  <FaStop size={20} className="invisible" />
                </button>
                <div className="absolute bottom-[15%] right-[11%] flex flex-col items-center gap-4 md:flex-row">
                  <div className="upload-btn-wrapper cursor-pointer">
                    <button className="flex items-center gap-2 rounded-full bg-black bg-opacity-60 p-3 text-xs font-semibold text-red-200 transition-all duration-500 ease-in-out hover:bg-red-700">
                      <img
                        src={FlipCamera}
                        alt="FlipCamera"
                        className="h-[30px]"
                      />
                    </button>
                  </div>
                </div>
                <div className="absolute bottom-0 flex h-20 w-full justify-center gap-5 rounded-tl-2xl rounded-tr-2xl bg-greyishV4 pt-3">
                  <Swiper
                    grabCursor={true}
                    slidesPerView={7}
                    spaceBetween={10}
                    centeredSlides={true}
                    className=""
                    modules={[Keyboard, Mousewheel]}
                    onSlideChange={handleSlideChange}
                    onSwiper={(swiper) => (sliderRef.current = swiper)}
                  >
                    {allTabs?.map((row, index) => (
                      <SwiperSlide
                        key={index}
                        className={`!w-fit ${
                          index === currentSlide
                            ? "text-xl text-[#F8BA34]"
                            : "text-xl text-[#A3A3A3]"
                        } `}
                      >
                        <p
                          onClick={() => {
                            handleSlideClick(index);
                          }}
                        >
                          {row}
                        </p>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </>
            )}
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default NotesWebcam;
